// @flow
import * as React from 'react';
import { withRouter } from 'react-router';
import deepEqual from 'react-fast-compare';
import queryString from 'query-string';
import { trackClicked } from 'utils/analytics';
import { connect } from 'react-redux';
import type { Location, RouterHistory } from 'react-router';
import { truncateToUTF8Bytes } from 'utils/string';
import { listingSearchHome } from 'utils/searchHomePages';
import { stringify } from 'utils/functional';
import { getParam } from 'utils/url';
import type { DisplayedType } from 'utils/constants';
import { listingTypesBySection } from 'utils/constants';
import SearchInputs from 'components/SearchInputs';
import type { SearchSection } from 'api/shared';
import type { State as MainSearch } from 'redux/main/reducers/mainSearch';
import type { CurrentUser } from 'api/currentUser';
import { isEnabled as daysEnabled } from 'utils/idealistdayevents';
import {
  initializeSearch as initializeSearchAction,
  initializeLandingPageSearch as initializeLandingPageSearchAction,
  uninitializeSearch as uninitializeSearchAction,
  setUserInitiatedSearch as setUserInitiatedSearchAction,
  setSearchFilter as setSearchFilterAction,
  setSearchQuery as setSearchQueryAction
} from '../redux/main/actions/mainSearch';
import {
  updateSearchLocation as updateSearchLocationAction,
  clearSearchLocation as clearSearchLocationAction
} from '../redux/main/actions/currentUser';
import { showModal as showModalAction } from '../redux/main/actions/modal';

const { useEffect, useState, useCallback } = React;

type OwnProps = {
  onSearch?: Object => void,
  searchSection: SearchSection,
  inPageHeader?: boolean,
  location: Location,
  history: RouterHistory
};

type Props = {
  ...$Exact<OwnProps>,
  searchFrom?: string,
  searchMode?: boolean,
  searchSection: SearchSection,
  type: ListingType,
  query: string,
  currentUser: CurrentUser,
  mainSearch: ?MainSearch,
  setUserInitiatedSearch: () => mixed,
  setSearchQuery: (string, boolean) => mixed,
  setSearchFilter: (string, mixed) => mixed,
  updateSearchLocation: (SearchLocation, boolean) => mixed,
  clearSearchLocation: boolean => mixed,
  initializeLandingPageSearch: Function,
  initializeSearch: Function,
  uninitializeSearch: () => void,
  defaultSearchType: ?ListingType,
  searchMode: boolean,
  showModal: Function
};

const SearchInputsContainer = (props: Props) => {
  const [listingTypes, setListingTypes] = useState<Array<DisplayedType>>(
    listingTypesBySection[props.searchSection]
  );

  useEffect(
    () => {
      if (daysEnabled() && props.searchSection === 'MAIN') {
        setListingTypes(
          listingTypesBySection[props.searchSection].concat([
            'IDEALIST_DAY_EVENT'
          ])
        );
      }
    },
    [props.searchSection]
  );
  const [type, setType] = useState<ListingType>(
    props.type || props.defaultSearchType || listingTypes[0]
  );
  const [query, setQuery] = useState(
    truncateToUTF8Bytes(
      props.query || getParam(props.location, 'q', true) || '',
      512
    )
  );
  const [enqueueSearch, setEnqueueSearch] = useState([false, null]);
  const [location, setLocation] = useState<?SearchLocation>(
    props.currentUser.searchLocation
  );

  useEffect(
    () => {
      if (props.defaultSearchType) {
        setType(props.defaultSearchType);
      }
    },
    [props.defaultSearchType]
  );
  useEffect(
    () => {
      setLocation(props.currentUser.searchLocation);
    },
    [props.currentUser.searchLocation]
  );

  const setListingType = setType;
  const setUserQuery = useCallback(
    (q: string) => {
      // Algolia has a max query length of 512 bytes
      setQuery(truncateToUTF8Bytes(q, 512));
    },
    [setQuery]
  );
  const setSearchLocation = (searchLocation: ?SearchLocation) => {
    setLocation(searchLocation);
    setEnqueueSearch([true, query]);
  };
  const search = useCallback(
    stateQuery => {
      const {
        clearSearchLocation,
        updateSearchLocation,
        setUserInitiatedSearch,
        history,
        searchFrom,
        searchMode,
        setSearchFilter,
        setSearchQuery
      } = props;
      const stateSearchLocation = location;
      const stateType = type;

      if (!deepEqual(props.currentUser.searchLocation, stateSearchLocation)) {
        if (stateSearchLocation) {
          updateSearchLocation(stateSearchLocation, true);
        } else {
          clearSearchLocation(true);
        }
      }
      if (stateType !== props.type) {
        setSearchFilter('type', stateType);
      }
      if (stateQuery !== props.query) {
        setSearchQuery(stateQuery, true);
      }

      const searchHome = listingSearchHome(stateType);
      if (searchHome && !searchMode) {
        if (stateQuery && stateQuery === props.query) {
          history.replace(`${searchHome}?q=${stateQuery}&searchMode=true`, {
            searchFrom
          });
        } else {
          history.push(`${searchHome}?q=${stateQuery}&searchMode=true`, {
            searchFrom
          });
        }
        setUserInitiatedSearch();
      }
    },
    [props, location, type]
  );

  const redirectTo = props.location
    ? `${props.location.pathname}${props.location.search}`
    : '';

  const clickLogin = (e: SyntheticEvent<>) => {
    e.preventDefault();
    trackClicked('Log In Search Hero');
    props.showModal('LOGIN', {
      redirectTo,
      manualLogin: true
    });
  };

  const clickSignup = (e: SyntheticEvent<>) => {
    e.preventDefault();
    trackClicked('Sign Up Search Hero');
    props.showModal('SIGNUP', {
      redirectTo
    });
  };

  const redirectParam = queryString.stringify({ to: redirectTo });

  useEffect(
    () => {
      const [enqueued, searchQuery] = enqueueSearch;
      if (enqueued) {
        setEnqueueSearch([false, null]);
        search(stringify(searchQuery));
      }
    },
    [enqueueSearch, props, search]
  );

  const clearSearchInput = useCallback(
    () => {
      setUserQuery('');
      if (props.searchMode) {
        search('');
      }
    },
    [setUserQuery, search, props.searchMode]
  );

  const selectedType =
    listingTypes.find(l => l === type) ||
    (listingTypes.includes('JOB') ? 'JOB' : listingTypes[0]);
  return (
    <SearchInputs
      listingTypes={listingTypes}
      selectedType={selectedType}
      onSearch={props.onSearch}
      query={query}
      searchLocation={location}
      setListingType={setListingType}
      setQuery={setUserQuery}
      clearSearchInput={clearSearchInput}
      redirectParam={redirectParam}
      setSearchLocation={setSearchLocation}
      search={search}
      placeholder={getText('Search by keyword, skill, or interest')}
      xsPlaceholder={getText('Keyword, skill, or interest')}
      inPageHeader={props.inPageHeader}
      clickLogin={clickLogin}
      clickSignup={clickSignup}
      showLogin={props.currentUser.isLoaded && !props.currentUser.id}
      currentUserLoaded={props.currentUser.isLoaded}
    />
  );
};

const mapStateToProps = ({ mainSearch, currentUser }) => ({
  mainSearch,
  query: mainSearch ? mainSearch.query : '',
  searchMode: mainSearch ? mainSearch.searchMode : false,
  type: mainSearch && mainSearch.filters && mainSearch.filters.type,
  currentUser
});

export default withRouter(
  connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps,
    // TODO: Figure out below, will solve most of our FlowFixMe statements
    // $FlowFixMe: for some reason, react-redux flow definition fails if we include the react-router Location type
    {
      setUserInitiatedSearch: setUserInitiatedSearchAction,
      setSearchFilter: setSearchFilterAction,
      setSearchQuery: setSearchQueryAction,
      updateSearchLocation: updateSearchLocationAction,
      clearSearchLocation: clearSearchLocationAction,
      initializeSearch: initializeSearchAction,
      initializeLandingPageSearch: initializeLandingPageSearchAction,
      uninitializeSearch: uninitializeSearchAction,
      showModal: showModalAction
    }
  )(SearchInputsContainer)
);
